import { Wifi } from 'lucide-react';
import { useChargerUptime } from '@/hooks/useChargerUptime';
import styles from '../DashboardPage.module.css';

/**
 * Charger uptime widget. Big percent number + 24h hourly
 * sparkline reconstructed from OcppMessage activity.
 *
 * Color thresholds match operator instinct: >90% = green
 * (healthy), 70-90% = amber (degraded), <70% = red (incident).
 *
 * Sparkline: pure SVG, no chart library. The platform's @evi/ui
 * pkg already ships chart components as plain SVG; recharts is
 * declared in package.json but never imported anywhere — we
 * follow the platform convention.
 */
export default function ChargerUptimeWidget() {
  const q = useChargerUptime();
  const data = q.data;

  return (
    <div className={styles.chartBody}>
      <div className={styles.chartHeader}>
        <div className={styles.chartIcon}><Wifi size={14} /></div>
        <p className={styles.chartTitle}>Charger uptime</p>
      </div>

      {q.isLoading && !data && (
        <div className={styles.chartPlaceholder}>
          <p className={styles.chartDesc}>Loading…</p>
        </div>
      )}

      {data && data.total_chargers === 0 && (
        <div className={styles.anomEmpty}>
          <span className={styles.anomEmptyIcon}><Wifi size={18} /></span>
          <p>
            {data.is_scoped
              ? 'No chargers in your scope.'
              : 'No chargers in this tenant.'}
          </p>
        </div>
      )}

      {data && data.total_chargers > 0 && (
        <>
          <div>
            <p className={`${styles.uptimeAmount} ${uptimeColorClass(data.percent_online_now)}`}>
              {Math.round(data.percent_online_now)}%
            </p>
            <p className={styles.uptimeSubtitle}>
              {Math.round((data.percent_online_now / 100) * data.total_chargers)} / {data.total_chargers} ONLINE
            </p>
          </div>
          {data.sparkline_buckets && (
            <>
              <Sparkline values={data.sparkline_buckets} />
              <div className={styles.uptimeSparklineCaption}>
                <span>24h ago</span>
                <span>now</span>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function uptimeColorClass(pct: number): string {
  if (pct >= 90) return styles.uptimeAmountGood ?? '';
  if (pct >= 70) return styles.uptimeAmountWarn ?? '';
  return styles.uptimeAmountDanger ?? '';
}

/**
 * Tiny SVG sparkline. Renders a 24-bucket polyline with a soft
 * fill underneath. Width auto-fills the parent; height fixed at
 * 36px via CSS. preserveAspectRatio='none' makes the polyline
 * stretch to the available width regardless of viewport.
 *
 * No chart library — values mapped to SVG coordinates by hand.
 * Domain locked to 0..100 (uptime is a percent), so the y-axis
 * doesn't autoscale and the sparkline reads consistently across
 * polls and across widgets ("100%" is always the top).
 */
function Sparkline({ values }: { values: number[] }) {
  if (values.length === 0) return null;
  // Internal viewport is normalized; SVG scales to fit the CSS
  // width. Width = (n-1) * step; using 100 = 24 * step ≈ a clean
  // grid step of ~4.17, but we only need the SHAPE — actual
  // numbers don't matter as long as they're consistent.
  const W = 100;
  const H = 100;
  const step = values.length > 1 ? W / (values.length - 1) : W;
  const pts = values.map((v, i) => {
    const x = i * step;
    // SVG y is top-down; flip so 100% sits at the top.
    const y = H - Math.max(0, Math.min(100, v));
    return [x, y] as const;
  });
  const linePoints = pts.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  // Fill polygon: line points + bottom-right + bottom-left to
  // close the area below the curve.
  const fillPoints = `${linePoints} ${W.toFixed(2)},${H} 0,${H}`;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className={styles.uptimeSparkline}
      role="img"
      aria-label="24-hour uptime sparkline"
    >
      <polygon className={styles.uptimeSparklineFill} points={fillPoints} />
      <polyline className={styles.uptimeSparklineStroke} points={linePoints} />
    </svg>
  );
}
